import { flowStepLabel } from '../_labels';
import { Frame } from '../atoms/Frame';
import { MiniHeader } from '../atoms/MiniHeader';
import { InstructionCard } from '../atoms/InstructionCard';
import { NumList } from '../atoms/NumList';
import { BulletGood, BulletBad } from '../atoms/Bullets';
import { WizardCtaBar } from '../../../components/atoms/WizardCtaBar';
import { StepStrip } from '../StepStrip';
import { useT } from '../../../i18n/useT';
import { P, S, T } from '../../../styles/tokens';
import type { Checkpoint, CheckpointAttempt, SlotFlowRun } from '../../../api/flow-types';

interface ExamplesMeta {
  good?: string[];
  bad?: string[];
}

interface TemplateProps {
  checkpoint: Checkpoint;
  attempt?: CheckpointAttempt;
  flowRun: SlotFlowRun;
  checkpoints: Checkpoint[];
  onCta: () => void;
}

export function InstructionScreen({ checkpoint, checkpoints, onCta }: TemplateProps) {
  const label = useT('flow.cta.openCamera');
  const meta = checkpoint.metadata as Record<string, unknown>;
  const examples = meta.examples as ExamplesMeta | undefined;
  const steps = Array.isArray(meta.steps) ? (meta.steps as string[]) : undefined;

  return (
    <Frame>
      <MiniHeader title={flowStepLabel(checkpoint.kind)} />

      {/* Step progress strip */}
      <div style={{ padding: '12px 16px 0' }}>
        <StepStrip current={checkpoint.position + 1} total={checkpoints.length} showCaption />
      </div>

      {/* Scrollable body */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '16px 16px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <InstructionCard>
          {/* Instructions heading — title3 (600 sans) */}
          {checkpoint.instructions_text && (
            <p
              style={{
                font: T.title3,
                color: S.label,
                marginBottom: 12,
                lineHeight: 1.2,
                margin: '0 0 12px',
              }}
            >
              {checkpoint.instructions_text}
            </p>
          )}

          {/* Numbered steps */}
          {steps && steps.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <NumList items={steps} />
            </div>
          )}

          {/* Good / bad examples */}
          {examples && (examples.good?.length || examples.bad?.length) ? (
            <div
              style={{
                marginTop: 4,
                paddingTop: 12,
                borderTop: `1px solid ${P.line}`,
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
              }}
            >
              <div
                style={{
                  font: T.caption,
                  fontWeight: 700,
                  color: S.label,
                  marginBottom: 4,
                }}
              >
                На фото должно быть:
              </div>
              {examples.good?.map((text, i) => (
                <BulletGood key={`good-${i}`} text={text} />
              ))}
              {examples.bad?.map((text, i) => (
                <BulletBad key={`bad-${i}`} text={text} />
              ))}
            </div>
          ) : null}
        </InstructionCard>
      </div>

      <WizardCtaBar label={label} onClick={onCta} />
    </Frame>
  );
}
